import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TimelineTrazabilidad from '../components/CamposDinamicos/TimelineTrazabilidad';
import camposDinamicosAPI from '../api/v1/camposDinamicosAPI';
import axios from 'axios';

const TrazabilidadOrden = () => {
  const { id_orden } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orden, setOrden] = useState(null);
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, [id_orden]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar información de la orden
      const ordenResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/ordenes/${id_orden}`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      setOrden(ordenResponse.data);

      // Cargar historial de valores
      const historialData = await camposDinamicosAPI.obtenerHistorialCompleto(
        parseInt(id_orden),
        user.token
      );
      setHistorial(historialData.historial || []);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar la información de la orden');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarPDF = () => {
    // TODO: Implementar exportación a PDF
    alert('Funcionalidad de exportación a PDF próximamente');
  };

  const calcularEstadisticas = () => {
    const totalEstados = historial.length;
    const estadosConValores = historial.filter(e => e.valores && e.valores.length > 0).length;
    const totalCamposCompletados = historial.reduce(
      (total, estado) => total + (estado.valores?.length || 0),
      0
    );

    return {
      totalEstados,
      estadosConValores,
      totalCamposCompletados
    };
  };

  const stats = historial.length > 0 ? calcularEstadisticas() : null;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink color="inherit" href="/home">
          Inicio
        </MuiLink>
        <MuiLink color="inherit" href="/home/ordenes">
          Órdenes
        </MuiLink>
        <Typography color="text.primary">Trazabilidad</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Trazabilidad de Orden #{id_orden}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Historial completo de valores de campos dinámicos
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportarPDF}
          >
            Exportar PDF
          </Button>
        </Box>
      </Box>

      {/* Información de la orden */}
      {orden && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información de la Orden
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {orden.nombre_cliente} {orden.apellido_cliente}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Local
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {orden.nombre_local || 'No especificado'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Estado Actual
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {orden.nombre_estado_servicio}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Fecha de Orden
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {new Date(orden.fecha_orden).toLocaleDateString('es-PE')}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {stats.totalEstados}
              </Typography>
              <Typography variant="body2">
                Estados Registrados
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {stats.estadosConValores}
              </Typography>
              <Typography variant="body2">
                Estados con Valores
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {stats.totalCamposCompletados}
              </Typography>
              <Typography variant="body2">
                Campos Completados
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Contenido */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Línea de Tiempo
            </Typography>
            <TimelineTrazabilidad historial={historial} />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default TrazabilidadOrden;
